import React, { useRef } from 'react';
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  Portal,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import Icon from 'components/icon2';
import styled from 'theme/styled';
import ListItemLink from 'components/material/list-item-link';
import { Apps, App } from 'core/types/app';
import p, { locale } from 'translation';
import NavigationProfileMenu from '../profile-menu';

interface Props {
  active?: Apps;
  apps: App[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function NavigationMobileMenu({
  active,
  apps,
  open,
  setOpen,
}: Props): JSX.Element {
  const ref = useRef<HTMLButtonElement | null>(null);

  return (
    <>
      <IconButton
        ref={ref}
        edge='end'
        onClick={() => setOpen(!open)}
        size='large'
      >
        <Icon icon={open ? 'times' : 'bars'} color='background.paper' />
      </IconButton>
      {open && (
        <Portal>
          <Submenu>
            <List className='app-list'>
              {apps.map(app => (
                <AppRow
                  className={app.id === active ? 'active' : ''}
                  key={app.id}
                  to={`/app/${app.id}`}
                  onClick={() => setOpen(false)}
                  divider={false}
                >
                  <ListItemAvatar>
                    <AppIcon
                      variant='rounded'
                      className={app.id === active ? 'active' : 'idle'}
                    >
                      <img src={app.icon} alt='' />
                    </AppIcon>
                  </ListItemAvatar>
                  <ListItemText>
                    <h2>{app.name}</h2>
                  </ListItemText>
                  <Icon icon='chevron-right' />
                </AppRow>
              ))}
            </List>
            <List>
              <ListItem
                button
                onClick={() => {
                  setOpen(false);
                  window.open(
                    `https://noisolation.helpdocs.io/l/${locale}/category/hsryhvtvdq-komp-pro`,
                    '_blank',
                  );
                }}
              >
                <Help>{p.t('common.help')}</Help>
              </ListItem>
            </List>

            <div className='profile'>
              <NavigationProfileMenu
                variant='dark'
                onClick={() => setOpen(false)}
              />
            </div>
          </Submenu>
        </Portal>
      )}
    </>
  );
}

const Submenu = styled.div`
  position: absolute;
  top: 80px;
  bottom: 0px;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.background.paper};
  z-index: 15000;

  h2 {
    margin: 0;
  }

  .app-list {
    background-color: ${({ theme }) => theme.palette.primary.light};
    color: ${({ theme }) => theme.palette.background.paper};

    .app-name {
      color: ${({ theme }) => theme.palette.primary.dark};
    }
  }

  .active {
    background-color: ${({ theme }) => theme.palette.primary.dark};
    color: ${({ theme }) => theme.palette.background.paper};
  }

  .profile {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    position: absolute;
    bottom: 0px;
    padding: ${({ theme }) => theme.spacing(2)};
  }
`;

const AppRow = styled(ListItemLink)`
  padding-top: 1em;
  padding-bottom: 1em;
`;

const Help = styled(ListItemText)`
  padding-left: 65px;
`;

const AppIcon = styled(Avatar)`
  margin-right: ${({ theme }) => theme.spacing(3)};

  &.idle {
    background: ${({ theme }) => theme.palette.common.black};
  }

  &.active {
    background-color: ${({ theme }) => theme.palette.primary.main};
  }
`;
